import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { PostListResponse } from '../../../server/response/responseScheme';
import { ChannelType } from '../../../types/channelTypes';
import List from '../../uikit/List';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import _ from 'lodash';
import { CommonLink, useCommonNavigate } from '../common/CommonLink';
import { CommonTimeFormat } from '../common/CommonDateFormat';
import webViewStore from '../../stores/webViewStore';

class ViewStore {
    readonly channelType: ChannelType;

    postList: PostListResponse = {
        isEnd: false,
        items: [],
    };
    isLoading = true;
    lastHash: string | null = null;

    constructor(channelType: ChannelType) {
        makeAutoObservable(this);
        this.channelType = channelType;
    }

    async fetchPostList(isForce = false) {
        this.isLoading = true;

        try {
            const postsResult = await apiClient.post.public.getPostList.$query(
                {
                    channelType: this.channelType,
                },
                {
                    force: isForce,
                },
            );

            runInAction(() => {
                this.postList = postsResult;
                this.lastHash = _.last(this.postList.items)?.hash || null;
            });
        } catch (error) {
            console.error('Failed to fetch post list:', error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    async fetchMorePostList() {
        if (this.isLoading) {
            return;
        }

        if (this.lastHash === null || this.postList.isEnd) {
            return;
        }

        this.isLoading = true;

        try {
            const morePosts = await apiClient.post.member.getPostMoreList.$query({
                channelType: this.channelType,
                fromHash: this.lastHash,
            });

            runInAction(() => {
                const prevItems = this.postList.items || [];
                this.postList = {
                    ...morePosts,
                    items: [...prevItems, ...morePosts.items],
                };
                this.lastHash = _.last(this.postList.items)?.hash || null;
            });
        } catch (error) {
            console.error('Failed to fetch more post list:', error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
}

const PostItem = observer(
    ({
        channelType,
        post,
    }: {
        channelType: ChannelType;
        post: PostListResponse['items'][number];
    }) => {
        const { navigate } = useCommonNavigate();

        return (
            <article
                className="list-col-grow cursor-pointer py-1"
                role="button"
                onClick={(e) => {
                    e.preventDefault();
                    navigate(`/channel/${channelType}/${post.hash}`);
                }}
            >
                <header>
                    <CommonLink
                        to={`/channel/${channelType}/${post.hash}`}
                        className="link link-hover block text-lg leading-tight font-semibold tracking-tight"
                        preventScrollReset={false}
                    >
                        {post.title}
                    </CommonLink>
                </header>
                <div className="text-base-content/70 mt-1 text-sm">
                    <div className="line-clamp-2">{post.summary}</div>
                    <footer className="text-base-content/40 mt-3.5 text-xs">
                        <CommonTimeFormat time={post.publishedAt} fromNow={true} />
                    </footer>
                </div>
            </article>
        );
    },
);

interface PostListProps {
    channelType: ChannelType;
}

const PostList = observer(({ channelType }: PostListProps) => {
    const viewStore = useMobxInstance(ViewStore, channelType);

    useEffect(() => {
        viewStore.fetchPostList();
    }, [viewStore]);

    useEffect(() => {
        if (!webViewStore.isWebView) {
            return;
        }

        const onPostCreated = async (data: { channelType: ChannelType }) => {
            if (data.channelType === channelType) {
                await viewStore.fetchPostList(true);
            }
        };

        webViewStore.addSyncUiHandler('postCreated', onPostCreated);

        return () => {
            webViewStore.removeSyncUiHandler('postCreated', onPostCreated);
        };
    }, [channelType, webViewStore.isWebView]);

    const hasMoreItems =
        !viewStore.isLoading && !_.isEmpty(viewStore.postList.items) && !viewStore.postList.isEnd;

    return (
        <>
            <List>
                {viewStore.postList.items.map((post) => (
                    <List.Item key={post.hash}>
                        <PostItem channelType={channelType} post={post} />
                    </List.Item>
                ))}
            </List>
            {hasMoreItems && (
                <footer className="mt-2">
                    <button
                        className="btn btn-block btn-soft btn-sm font-normal"
                        onClick={(e) => {
                            e.preventDefault();
                            viewStore.fetchMorePostList();
                        }}
                    >
                        {viewStore.isLoading ? (
                            <span className="loading loading-ring loading-sm"></span>
                        ) : (
                            '더보기'
                        )}
                    </button>
                </footer>
            )}
            {viewStore.postList.items.length === 0 && (
                <div className="text-base-content/50 py-8 text-center">
                    아직 작성된 글이 없습니다.
                </div>
            )}
        </>
    );
});

export default PostList;
